import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ArrowRight,
  Newspaper,
  Calendar,
  ArrowLeft,
  Tag,
  Twitter,
  Linkedin,
  Copy,
  CheckCircle,
  TrendingUp,
  User
} from 'lucide-react';

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const API_KEY = process.env.REACT_APP_API_KEY || '';

async function fetchWithAuth(url, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
  }
  return fetch(url, { ...options, headers });
}

// =============================================================================
// NEWS LISTING PAGE (/news)
// =============================================================================

export function NewsPage({ Header, Footer }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const pageSize = 6;

  useEffect(() => {
    async function loadStories() {
      setLoading(true);
      try {
        let url = `${BACKEND_API_URL}/api/stories?limit=50`;
        if (category !== 'all') url += `&category=${category}`;

        const response = await fetchWithAuth(url);
        const data = await response.json();
        if (data.success) {
          setStories(data.stories);
        }
      } catch (error) {
        console.error('Failed to load stories:', error);
      } finally {
        setLoading(false);
      }
    }
    loadStories();
  }, [category]);

  const categories = [
    { key: 'all', label: 'All News' },
    { key: 'announcement', label: 'Announcements' },
    { key: 'ecosystem', label: 'Ecosystem' },
    { key: 'tutorial', label: 'Tutorials' },
    { key: 'analysis', label: 'Analysis' },
    { key: 'partnership', label: 'Partnerships' }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (cat) => {
    const colors = {
      announcement: 'bg-blue-100 text-blue-700 border-blue-200',
      ecosystem: 'bg-green-100 text-green-700 border-green-200',
      tutorial: 'bg-purple-100 text-purple-700 border-purple-200',
      analysis: 'bg-orange-100 text-orange-700 border-orange-200',
      partnership: 'bg-pink-100 text-pink-700 border-pink-200'
    };
    return colors[cat] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const paginatedStories = stories.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(stories.length / pageSize);
  const featuredStory = stories.find(s => s.featured) || stories[0];

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Helmet>
        <title>News | 8004 Agents - ERC-8004 Ecosystem Updates</title>
        <meta name="description" content="Stay updated with the latest ERC-8004 ecosystem news, AI agent announcements, and blockchain developments." />
        <link rel="canonical" href="https://8004agents.ai/news" />
      </Helmet>

      <Header
        stats={null}
        chainFilter="all"
        onChainFilterChange={() => { }}
        chains={[]}
        refreshing={false}
        onRefresh={() => { }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Explorer
        </Link>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Hero Section */}
        <div className="mb-4 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
            <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg sm:rounded-xl">
              <Newspaper className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900">ERC-8004 News</h1>
              <p className="text-xs sm:text-base text-gray-600">Latest updates from the AI agent ecosystem</p>
            </div>
          </div>
        </div>

        {/* Featured Story */}
        {featuredStory && !loading && (
          <Link
            to={`/news/${featuredStory.slug}`}
            className="block mb-4 sm:mb-8 group"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 sm:w-64 h-32 sm:h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 sm:w-48 h-24 sm:h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2 sm:mb-4">
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/20 rounded-full text-[10px] sm:text-xs font-bold uppercase">
                    Featured
                  </span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-white/10 rounded-full text-[10px] sm:text-xs">
                    {featuredStory.category}
                  </span>
                </div>

                <h2 className="text-lg sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 group-hover:underline line-clamp-2">
                  {featuredStory.title}
                </h2>

                <p className="text-blue-100 mb-2 sm:mb-4 text-sm sm:text-base line-clamp-2 sm:line-clamp-none max-w-5xl">
                  {featuredStory.summary || featuredStory.subtitle}
                </p>

                <div className="flex items-center gap-3 sm:gap-4 text-blue-200 text-xs sm:text-sm">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">{formatDate(featuredStory.published_at)}</span>
                    <span className="sm:hidden">{new Date(featuredStory.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    {featuredStory.read_time_minutes} min
                  </div>
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Category Filter */}
        <div className="mb-4 sm:mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {categories.map(cat => (
              <button
                key={cat.key}
                onClick={() => { setCategory(cat.key); setPage(1); }}
                className={`flex-shrink-0 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition whitespace-nowrap ${category === cat.key
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stories Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 sm:p-6 animate-pulse">
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/4 mb-3 sm:mb-4" />
                <div className="h-5 sm:h-6 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-full mb-3 sm:mb-4" />
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {paginatedStories.map(story => (
                <Link
                  key={story.id}
                  to={`/news/${story.slug}`}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all group"
                >
                  {story.image_url && (
                    <div className="h-32 sm:h-40 bg-gray-100 overflow-hidden">
                      <img
                        src={story.image_url}
                        alt={story.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}

                  <div className="p-3 sm:p-5">
                    <div className="flex items-center gap-2 mb-2 sm:mb-3">
                      <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium border ${getCategoryColor(story.category)}`}>
                        {story.category}
                      </span>
                      <span className="text-gray-400 text-[10px] sm:text-xs">
                        {new Date(story.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>

                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                      {story.title}
                    </h3>

                    <p className="text-gray-600 text-xs sm:text-sm line-clamp-2 mb-3 sm:mb-4">
                      {story.summary || story.subtitle}
                    </p>

                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        {story.read_time_minutes} min
                      </div>
                      <span className="text-blue-500 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 sm:gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-2 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">←</span>
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg text-xs sm:text-sm font-medium ${page === pageNum
                          ? 'bg-blue-500 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-2 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg text-xs sm:text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">→</span>
                </button>
              </div>
            )}
          </>
        )}
      </main>

      <Footer stats={null} agents={[]} />
    </div>
  );
}

// =============================================================================
// SINGLE ARTICLE PAGE (/news/:slug)
// =============================================================================

export function ArticlePage({ Header, Footer }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function loadStory() {
      setLoading(true);
      try {
        const response = await fetchWithAuth(`${BACKEND_API_URL}/api/stories/${slug}`);
        const data = await response.json();

        if (data.success) {
          setStory(data.story);

          const relatedResponse = await fetchWithAuth(`${BACKEND_API_URL}/api/stories/${slug}/related`);
          const relatedData = await relatedResponse.json();
          if (relatedData.success) {
            setRelated(relatedData.stories);
          }
        } else {
          navigate('/news');
        }
      } catch (error) {
        console.error('Failed to load story:', error);
        navigate('/news');
      } finally {
        setLoading(false);
      }
    }
    loadStory();
  }, [slug, navigate]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = `https://8004agents.ai/news/${slug}`;

  const shareOnTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(story.title)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const shareOnLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderContent = (content) => {
    if (!content) return null;

    return content
      .split('\n\n')
      .map((paragraph, index) => {
        if (paragraph.startsWith('### ')) {
          return <h3 key={index} className="text-xl font-bold text-gray-900 mt-8 mb-4">{paragraph.replace('### ', '')}</h3>;
        }
        if (paragraph.startsWith('## ')) {
          return <h2 key={index} className="text-2xl font-bold text-gray-900 mt-10 mb-4">{paragraph.replace('## ', '')}</h2>;
        }
        if (paragraph.startsWith('# ')) {
          return <h1 key={index} className="text-3xl font-bold text-gray-900 mt-10 mb-4">{paragraph.replace('# ', '')}</h1>;
        }

        if (paragraph.includes('\n- ') || paragraph.startsWith('- ')) {
          const items = paragraph.split('\n').filter(line => line.startsWith('- '));
          return (
            <ul key={index} className="list-disc pl-6 mb-4 space-y-2">
              {items.map((item, i) => (
                <li key={i} className="text-gray-700">{item.replace('- ', '')}</li>
              ))}
            </ul>
          );
        }

        if (/^\d+\. /.test(paragraph)) {
          const items = paragraph.split('\n').filter(line => /^\d+\. /.test(line));
          return (
            <ol key={index} className="list-decimal pl-6 mb-4 space-y-2">
              {items.map((item, i) => (
                <li key={i} className="text-gray-700">{item.replace(/^\d+\. /, '')}</li>
              ))}
            </ol>
          );
        }

        let html = paragraph
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

        return <p key={index} className="text-gray-700 leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: html }} />;
      });
  };

  const getCategoryColor = (cat) => {
    const colors = {
      announcement: 'bg-blue-100 text-blue-700',
      ecosystem: 'bg-green-100 text-green-700',
      tutorial: 'bg-purple-100 text-purple-700',
      analysis: 'bg-orange-100 text-orange-700',
      partnership: 'bg-pink-100 text-pink-700'
    };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!story) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <Helmet>
        <title>{story.title} | 8004 Agents News</title>
        <meta name="description" content={story.summary || story.subtitle} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:title" content={story.title} />
        <meta property="og:description" content={story.summary || story.subtitle} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={shareUrl} />
        {story.image_url && <meta property="og:image" content={story.image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Header
        stats={null}
        chainFilter="all"
        onChainFilterChange={() => { }}
        chains={[]}
        refreshing={false}
        onRefresh={() => { }}
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
          <Link to="/" className="hover:text-blue-600">Home</Link>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          <Link to="/news" className="hover:text-blue-600">News</Link>
          <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="text-gray-900 truncate max-w-[120px] sm:max-w-[200px]">{story.title}</span>
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-xl sm:rounded-2xl border border-gray-200 overflow-hidden mb-6 sm:mb-8">
          {story.image_url && (
            <div className="h-48 sm:h-64 md:h-80 bg-gray-100">
              <img
                src={story.image_url}
                alt={story.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-4 sm:p-6 md:p-10">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold uppercase ${getCategoryColor(story.category)}`}>
                {story.category}
              </span>
              <div className="flex items-center gap-1 text-gray-500 text-xs sm:text-sm">
                <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{formatDate(story.published_at)}</span>
                <span className="sm:hidden">{new Date(story.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-xs sm:text-sm">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                {story.read_time_minutes} min
              </div>
            </div>

            {/* Title */}
            <h1 className="text-xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              {story.title}
            </h1>

            {/* Subtitle */}
            {story.subtitle && (
              <p className="text-base sm:text-xl text-gray-600 mb-4 sm:mb-6">
                {story.subtitle}
              </p>
            )}

            {/* Author & Share */}
            <div className="flex items-center justify-between py-3 sm:py-4 border-t border-b border-gray-200 mb-6 sm:mb-8">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div>
                  <div className="font-medium text-gray-900 text-sm sm:text-base">{story.author || 'ERC-8004 News'}</div>
                  <div className="text-xs sm:text-sm text-gray-500">Ecosystem Reporter</div>
                </div>
              </div>

              <div className="flex items-center gap-1 sm:gap-2">
                <button
                  onClick={shareOnTwitter}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-500"
                  title="Share on Twitter"
                >
                  <Twitter className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={shareOnLinkedIn}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-blue-700"
                  title="Share on LinkedIn"
                >
                  <Linkedin className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <button
                  onClick={copyLink}
                  className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition text-gray-500 hover:text-gray-900"
                  title="Copy link"
                >
                  {copied ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" /> : <Copy className="w-4 h-4 sm:w-5 sm:h-5" />}
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-sm sm:prose-lg max-w-none">
              {renderContent(story.content)}
            </div>

            {/* Tags */}
            {story.tags && story.tags.length > 0 && (
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  {story.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 text-gray-600 rounded-full text-xs sm:text-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>

        {/* Related Stories */}
        {related.length > 0 && (
          <div className="mb-6 sm:mb-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
              Related Stories
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {related.map(item => (
                <Link
                  key={item.id}
                  to={`/news/${item.slug}`}
                  className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 hover:shadow-md hover:border-blue-300 transition group"
                >
                  <span className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium ${getCategoryColor(item.category)}`}>
                    {item.category}
                  </span>
                  <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mt-2 line-clamp-2 group-hover:text-blue-600">
                    {item.title}
                  </h3>
                  <p className="text-[10px] sm:text-xs text-gray-500 mt-2">
                    {new Date(item.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Back to News */}
        <Link
          to="/news"
          className="inline-flex items-center gap-1 sm:gap-2 text-blue-600 hover:text-blue-700 font-medium text-sm sm:text-base"
        >
          <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
          Back to all news
        </Link>
      </main>

      <Footer stats={null} agents={[]} />
    </div>
  );
}

export default NewsPage;